"use client";

import { useEffect, useMemo, useRef, useState, type MutableRefObject } from "react";
import { useRouter } from "next/navigation";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import styles from "./login.module.css";

type ErrorDetailItem = { loc?: Array<string | number>; msg?: string };
type ErrorPayload = {
  detail?: unknown;
  error?: unknown;
  access_token?: string;
  user?: { email?: string; verified?: boolean };
  verified?: boolean;
};

type StatusType = "idle" | "loading" | "error" | "success";
type AuthMode = "login" | "register";
type LoginStage = "form" | "verify";
type ResetStage = "email" | "confirm";

const CODE_LENGTH = 6;
const RESEND_SECONDS = 60;

const extractErrorMessage = (payload: ErrorPayload, fallback: string) => {
  const detail = payload.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (!item || typeof item !== "object") return "Invalid";
        const entry = item as ErrorDetailItem;
        const loc = entry.loc?.[1];
        const prefix = loc ? `${loc}: ` : "";
        return `${prefix}${entry.msg ?? "Invalid"}`;
      })
      .join(", ");
  }
  if (payload.error !== undefined) {
    return typeof payload.error === "string" ? payload.error : JSON.stringify(payload.error);
  }
  return fallback;
};

function StatusMessage({ text, type }: { text: string; type: StatusType }) {
  if (!text || type === "idle") return null;
  const cls =
    type === "loading"
      ? styles["statusMessage--loading"]
      : type === "error"
        ? styles["statusMessage--error"]
        : styles["statusMessage--success"];
  return (
    <div
      className={`${styles.statusMessage} ${cls}`}
      role={type === "error" ? "alert" : "status"}
      aria-live={type === "error" ? "assertive" : "polite"}
    >
      {text}
    </div>
  );
}

function sanitizeCode(value: string) {
  return value.replace(/\D/g, "").slice(0, CODE_LENGTH);
}

export default function LoginPage() {
  const router = useRouter();
  const { login } = useAuth();
  const [mode, setMode] = useState<AuthMode>("login");
  const [stage, setStage] = useState<LoginStage>("form");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [heldCredentials, setHeldCredentials] = useState<{ email: string; password: string } | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [statusText, setStatusText] = useState("");
  const [statusType, setStatusType] = useState<StatusType>("idle");
  const [codeDigits, setCodeDigits] = useState<string[]>(Array(CODE_LENGTH).fill(""));
  const [verifyStatusText, setVerifyStatusText] = useState("");
  const [verifyStatusType, setVerifyStatusType] = useState<StatusType>("idle");
  const [resendCountdown, setResendCountdown] = useState(0);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetStage, setResetStage] = useState<ResetStage>("email");
  const [resetEmail, setResetEmail] = useState("");
  const [resetCodeDigits, setResetCodeDigits] = useState<string[]>(Array(CODE_LENGTH).fill(""));
  const [resetPassword, setResetPassword] = useState("");
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetStatusText, setResetStatusText] = useState("");
  const [resetStatusType, setResetStatusType] = useState<StatusType>("idle");
  const verifyInputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const resetInputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const code = useMemo(() => codeDigits.join(""), [codeDigits]);
  const resetCode = useMemo(() => resetCodeDigits.join(""), [resetCodeDigits]);

  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = window.setTimeout(() => setResendCountdown((value) => value - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendCountdown]);

  const setStatus = (text: string, type: StatusType) => {
    setStatusText(text);
    setStatusType(type);
  };

  const resetVerifyInputs = () => {
    setCodeDigits(Array(CODE_LENGTH).fill(""));
    setVerifyStatusText("");
    setVerifyStatusType("idle");
    window.setTimeout(() => verifyInputRefs.current[0]?.focus(), 0);
  };

  const completeLogin = async (userEmail: string, userPassword: string) => {
    const res = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: userEmail, password: userPassword, client: "web" }),
    });
    const json = (await res.json()) as ErrorPayload;
    if (!res.ok || !json.access_token) {
      throw new Error(extractErrorMessage(json, "Email verified. Please sign in."));
    }
    login(json.access_token, json.user?.email || userEmail);
    router.push("/portfolio");
  };

  const beginVerification = (userEmail: string, userPassword: string, message: string) => {
    setHeldCredentials({ email: userEmail, password: userPassword });
    setStage("verify");
    setStatus(message, "success");
    setResendCountdown(RESEND_SECONDS);
    resetVerifyInputs();
  };

  const handleSubmit = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) { setStatus("Enter your email address.", "error"); return; }
    if (password.length < 6) { setStatus("Password must be at least 6 characters.", "error"); return; }
    if (password.length > 20) { setStatus("Password must be at most 20 characters.", "error"); return; }
    setStatus(mode === "login" ? "Signing in..." : "Creating account...", "loading");
    try {
      const res = await fetch(`/api/auth/${mode}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmedEmail, password, client: "web" }),
      });
      const json = (await res.json()) as ErrorPayload;
      const message = extractErrorMessage(json, "Sign-in failed. Please try again.");
      if (!res.ok) {
        if (res.status === 403 && message.toLowerCase().includes("email not verified")) {
          beginVerification(trimmedEmail, password, "Email not verified. We sent you a fresh 6-digit code.");
        } else {
          setStatus(message, "error");
        }
        return;
      }
      if (json.access_token) {
        login(json.access_token, json.user?.email || trimmedEmail);
        setStatus("Signed in successfully.", "success");
        router.push("/portfolio");
        return;
      }
      if (mode === "register" && (json.verified === false || json.user?.verified === false || !json.access_token)) {
        beginVerification(trimmedEmail, password, "Account created. Check your email for the 6-digit code.");
        return;
      }
      setStatus("Sign-in failed. Please try again.", "error");
    } catch {
      setStatus("Network error. Please check your connection and try again.", "error");
    }
  };

  const handleVerify = async (nextCode = code) => {
    if (!heldCredentials) return;
    if (nextCode.length !== CODE_LENGTH) {
      setVerifyStatusText("Enter the 6-digit code.");
      setVerifyStatusType("error");
      return;
    }
    setVerifyStatusText("Verifying...");
    setVerifyStatusType("loading");
    try {
      const res = await fetch("/api/auth/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: heldCredentials.email, code: nextCode, client: "web" }),
      });
      const json = (await res.json()) as ErrorPayload;
      if (!res.ok) {
        setCodeDigits(Array(CODE_LENGTH).fill(""));
        setVerifyStatusText(extractErrorMessage(json, "Verification failed. Check the code and try again."));
        setVerifyStatusType("error");
        verifyInputRefs.current[0]?.focus();
        return;
      }
      setVerifyStatusText("Email verified. Signing you in...");
      setVerifyStatusType("success");
      await completeLogin(heldCredentials.email, heldCredentials.password);
    } catch (error) {
      setVerifyStatusText(error instanceof Error ? error.message : "Verification failed. Please try again.");
      setVerifyStatusType("error");
    }
  };

  const handleResendCode = async () => {
    if (!heldCredentials || resendCountdown > 0) return;
    setVerifyStatusText("Sending a new code...");
    setVerifyStatusType("loading");
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...heldCredentials, client: "web" }),
      });
      const json = (await res.json()) as ErrorPayload;
      const message = extractErrorMessage(json, "Could not resend the code. Please try again.");
      if (res.status === 403 && message.toLowerCase().includes("email not verified")) {
        setResendCountdown(RESEND_SECONDS);
        resetVerifyInputs();
        setVerifyStatusText("New code sent.");
        setVerifyStatusType("success");
        return;
      }
      if (res.ok && json.access_token) {
        login(json.access_token, json.user?.email || heldCredentials.email);
        router.push("/portfolio");
        return;
      }
      setVerifyStatusText(message);
      setVerifyStatusType("error");
    } catch {
      setVerifyStatusText("Could not resend the code. Please try again.");
      setVerifyStatusType("error");
    }
  };

  const updateCodeDigit = (
    index: number,
    value: string,
    setter: (next: string[]) => void,
    refs: MutableRefObject<Array<HTMLInputElement | null>>,
    onComplete: (nextCode: string) => void,
  ) => {
    const clean = sanitizeCode(value);
    const next = Array(CODE_LENGTH).fill("");
    if (clean.length > 1) {
      clean.split("").forEach((digit, digitIndex) => {
        if (digitIndex < CODE_LENGTH) next[digitIndex] = digit;
      });
    } else {
      const current = refs.current.map((input) => input?.value || "");
      current[index] = clean;
      current.forEach((digit, digitIndex) => {
        next[digitIndex] = sanitizeCode(digit).slice(0, 1);
      });
    }
    setter(next);
    const joined = next.join("");
    if (joined.length === CODE_LENGTH) {
      onComplete(joined);
    } else {
      const nextIndex = Math.min(clean.length > 1 ? clean.length : index + 1, CODE_LENGTH - 1);
      window.setTimeout(() => refs.current[nextIndex]?.focus(), 0);
    }
  };

  const handleResetRequest = async () => {
    const trimmedEmail = resetEmail.trim();
    if (!trimmedEmail) { setResetStatusText("Enter your email address."); setResetStatusType("error"); return; }
    setResetStatusText("Sending reset code...");
    setResetStatusType("loading");
    try {
      const res = await fetch("/api/auth/reset/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmedEmail, client: "web" }),
      });
      const json = (await res.json()) as ErrorPayload;
      if (!res.ok) {
        setResetStatusText(extractErrorMessage(json, "Request failed. Please try again."));
        setResetStatusType("error");
        return;
      }
      setResetEmail(trimmedEmail);
      setResetStage("confirm");
      setResetCodeDigits(Array(CODE_LENGTH).fill(""));
      setResetStatusText("If that email exists, a reset code was sent.");
      setResetStatusType("success");
      window.setTimeout(() => resetInputRefs.current[0]?.focus(), 0);
    } catch {
      setResetStatusText("Request failed. Please try again.");
      setResetStatusType("error");
    }
  };

  const handleResetConfirm = async (nextCode = resetCode) => {
    if (nextCode.length !== CODE_LENGTH) { setResetStatusText("Enter the 6-digit code."); setResetStatusType("error"); return; }
    if (resetPassword.length < 6) { setResetStatusText("Password must be at least 6 characters."); setResetStatusType("error"); return; }
    if (resetPassword.length > 20) { setResetStatusText("Password must be at most 20 characters."); setResetStatusType("error"); return; }
    setResetStatusText("Updating password...");
    setResetStatusType("loading");
    try {
      const res = await fetch("/api/auth/reset/confirm", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: resetEmail.trim(), code: nextCode, new_password: resetPassword, client: "web" }),
      });
      const json = (await res.json()) as ErrorPayload;
      if (!res.ok) {
        setResetCodeDigits(Array(CODE_LENGTH).fill(""));
        setResetStatusText(extractErrorMessage(json, "Reset failed. Check the code and try again."));
        setResetStatusType("error");
        resetInputRefs.current[0]?.focus();
        return;
      }
      setResetOpen(false);
      setResetStage("email");
      setResetPassword("");
      setResetCodeDigits(Array(CODE_LENGTH).fill(""));
      setMode("login");
      setStage("form");
      setStatus("Password updated. You can now sign in.", "success");
      setResetStatusText("");
      setResetStatusType("idle");
    } catch {
      setResetStatusText("Reset failed. Please try again.");
      setResetStatusType("error");
    }
  };

  const renderCodeInputs = (
    digits: string[],
    refs: MutableRefObject<Array<HTMLInputElement | null>>,
    onChange: (index: number, value: string) => void,
    label: string,
  ) => (
    <div className={styles.codeGroup} aria-label={label}>
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(input) => { refs.current[index] = input; }}
          className={styles.codeInput}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={CODE_LENGTH}
          value={digit}
          onChange={(event) => onChange(index, event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Backspace" && !digits[index] && index > 0) {
              refs.current[index - 1]?.focus();
            }
          }}
        />
      ))}
    </div>
  );

  return (
    <div className={styles.shell}>
      <div className={styles.card}>
        <h1 className={styles.heading}>Account</h1>
        <p className={styles.subheading}>Sign in to track your portfolio and daily trades.</p>

        {stage === "form" ? (
          <>
            <div className={styles.modeTabs} role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={mode === "login"}
                className={`${styles.modeTab}${mode === "login" ? ` ${styles["modeTab--active"]}` : ""}`}
                onClick={() => { setMode("login"); setStatus("", "idle"); }}
              >
                Sign In
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mode === "register"}
                className={`${styles.modeTab}${mode === "register" ? ` ${styles["modeTab--active"]}` : ""}`}
                onClick={() => { setMode("register"); setStatus("", "idle"); }}
              >
                Create Account
              </button>
            </div>

            <form onSubmit={(event) => { event.preventDefault(); handleSubmit(); }} className={styles.form} noValidate>
              <div className={styles.fieldGroup}>
                <label htmlFor="login-email" className={styles.label}>
                  Email<span className={styles.required} aria-hidden="true">*</span>
                </label>
                <input
                  id="login-email"
                  type="email"
                  className="ui-input"
                  placeholder="you@example.com"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label htmlFor="login-password" className={styles.label}>
                  Password<span className={styles.required} aria-hidden="true">*</span>
                </label>
                <div className={styles.passwordWrapper}>
                  <input
                    id="login-password"
                    type={showPassword ? "text" : "password"}
                    className="ui-input"
                    placeholder="6-20 characters"
                    autoComplete={mode === "login" ? "current-password" : "new-password"}
                    required
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                  <button
                    type="button"
                    className={styles.passwordToggle}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowPassword((value) => !value)}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <StatusMessage text={statusText} type={statusType} />

              <button type="submit" className={styles.submitBtn} disabled={statusType === "loading"}>
                {mode === "login" ? "Sign In" : "Create Account"}
              </button>
            </form>

            <div className={styles.forgotWrap}>
              <button
                type="button"
                className={styles.forgotBtn}
                onClick={() => {
                  setResetOpen((value) => !value);
                  setResetStatusText("");
                  setResetStatusType("idle");
                }}
              >
                {resetOpen ? "Hide password reset" : "Forgot password?"}
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={(event) => { event.preventDefault(); handleVerify(); }} className={styles.form} noValidate>
            <p className={styles.subSectionLabel}>Verify Your Email</p>
            <p className={styles.helperText}>
              Enter the 6-digit code sent to {heldCredentials?.email || email.trim()}.
            </p>
            {renderCodeInputs(codeDigits, verifyInputRefs, (index, value) => {
              updateCodeDigit(index, value, setCodeDigits, verifyInputRefs, handleVerify);
            }, "Verification code")}
            <button type="submit" className={styles.submitBtn} disabled={verifyStatusType === "loading"}>
              Verify Email
            </button>
            <button
              type="button"
              className={styles.forgotBtn}
              onClick={handleResendCode}
              disabled={resendCountdown > 0 || verifyStatusType === "loading"}
            >
              {resendCountdown > 0 ? `Resend code in ${resendCountdown}s` : "Resend code"}
            </button>
            <button
              type="button"
              className={styles.forgotBtn}
              onClick={() => { setStage("form"); setStatus("", "idle"); }}
            >
              Back to sign in
            </button>
            <StatusMessage text={verifyStatusText || statusText} type={verifyStatusText ? verifyStatusType : statusType} />
          </form>
        )}

        {resetOpen && stage === "form" && (
          <>
            <hr className={styles.divider} />
            <p className={styles.subSectionLabel}>Reset Password</p>
            <div className={styles.forgotSection}>
              {resetStage === "email" ? (
                <form onSubmit={(event) => { event.preventDefault(); handleResetRequest(); }} className={styles.form} noValidate>
                  <div className={styles.fieldGroup}>
                    <label htmlFor="reset-email" className={styles.label}>Email address</label>
                    <input
                      id="reset-email"
                      type="email"
                      className="ui-input"
                      placeholder="you@example.com"
                      autoComplete="email"
                      value={resetEmail}
                      onChange={(event) => setResetEmail(event.target.value)}
                    />
                  </div>
                  <button type="submit" className={styles.submitBtn} disabled={resetStatusType === "loading"}>
                    Send Reset Code
                  </button>
                </form>
              ) : (
                <form onSubmit={(event) => { event.preventDefault(); handleResetConfirm(); }} className={styles.form} noValidate>
                  <p className={styles.helperText}>Enter the reset code sent to {resetEmail} and choose a new password.</p>
                  {renderCodeInputs(resetCodeDigits, resetInputRefs, (index, value) => {
                    updateCodeDigit(index, value, setResetCodeDigits, resetInputRefs, () => {});
                  }, "Reset code")}
                  <div className={styles.fieldGroup}>
                    <label htmlFor="reset-password" className={styles.label}>New password</label>
                    <div className={styles.passwordWrapper}>
                      <input
                        id="reset-password"
                        type={showResetPassword ? "text" : "password"}
                        className="ui-input"
                        placeholder="6-20 characters"
                        autoComplete="new-password"
                        value={resetPassword}
                        onChange={(event) => setResetPassword(event.target.value)}
                      />
                      <button
                        type="button"
                        className={styles.passwordToggle}
                        aria-label={showResetPassword ? "Hide password" : "Show password"}
                        onClick={() => setShowResetPassword((value) => !value)}
                      >
                        {showResetPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <button type="submit" className={styles.submitBtn} disabled={resetStatusType === "loading"}>
                    Set New Password
                  </button>
                </form>
              )}

              <StatusMessage text={resetStatusText} type={resetStatusType} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
